import React from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Image as ImageIcon } from "lucide-react";
import { logger } from "@/lib/logger";

export default function Assets() {
  const { projectId } = useParams();
  const [assets, setAssets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");

  React.useEffect(() => {
    (async () => {
      try {
        // Best-effort: fetch across all shots/characters/locations
        // For MVP we'll list from the shot / character / location asset ids
        const [shots, chars, locs] = await Promise.all([
          api.get(`/storyboards/project/${projectId}`),
          api.get(`/projects/${projectId}/characters`),
          api.get(`/projects/${projectId}/locations`),
        ]);
        const ids = [
          ...shots.data.filter((s) => s.image_asset_id).map((s) => ({ id: s.image_asset_id, kind: "storyboard", label: `Shot ${s.shot_number}` })),
          ...chars.data.filter((c) => c.portrait_asset_id).map((c) => ({ id: c.portrait_asset_id, kind: "character", label: c.name })),
          ...locs.data.filter((l) => l.image_asset_id).map((l) => ({ id: l.image_asset_id, kind: "location", label: l.name })),
        ];
        const results = await Promise.all(ids.map(async (x) => {
          try {
            const r = await api.get(`/storyboards/asset/${x.id}`);
            return { ...r.data, __label: x.label };
          } catch { return null; }
        }));
        setAssets(results.filter(Boolean));
      } catch (err) {
        logger.warn("assets: could not load asset list", err);
      }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  const shown = filter === "all" ? assets : assets.filter((a) => a.kind === filter);

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Asset Vault</div>
        <h1 className="font-editorial text-4xl mb-6">Generated assets</h1>

        <div className="flex gap-2 mb-6">
          {["all", "character", "location", "storyboard"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${filter === f ? "border-[#D4AF37] bg-[#141414] text-[#FFFFF0]" : "border-[#27272A] text-[#A1A1AA]"}`}
              data-testid={`asset-filter-${f}`}>
              {f}
            </button>
          ))}
        </div>

        {loading ? <div className="text-[#A1A1AA]">Loading…</div> :
          shown.length === 0 ? (
            <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
              <ImageIcon size={28} strokeWidth={1.5} className="mx-auto text-[#D4AF37]" />
              <div className="font-editorial text-2xl mt-3">No assets yet.</div>
              <p className="text-sm text-[#A1A1AA] mt-2">Generate character portraits, location art or storyboard frames &mdash; they will appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {shown.map((a) => (
                <div key={a.id} className="border border-[#1E1E1E] bg-[#141414]" data-testid={`asset-tile-${a.id}`}>
                  <div className="aspect-square bg-[#0A0A0A] overflow-hidden">
                    <img src={a.data_url} alt={a.__label} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{a.kind}</div>
                    <div className="text-sm text-[#E4E4E7] truncate">{a.__label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </DashboardLayout>
  );
}
